"use client";
import { useSpring, animated, useInView } from '@react-spring/web';
import { Calendar, Map, Hotel, Coffee, Users, ChevronRight, Monitor, AlertCircle } from 'lucide-react';

export default function BonusesSection() {
  const bonuses = [
    {
      icon: Calendar,
      title: "MENTORIA 1:1 CON TOÑO",
      description: "Una sesión especial de 2 horas con Toño Galán sobre cómo construir un negocio rentable como coach. Solo para participantes.",
      value: "300€",
      highlight: "Valorado en"
    },
    {
      icon: Map,
      title: "Zinquo premium",
      description: "El gimnasio online donde entrenas tus valores diariamente. Mantén viva la transformación con ejercicios, comunidad y recursos ilimitados.",
      value: "600€",
      highlight: "Valorado en"
    }
  ];

  // Move all hooks to top level
  const [headerRef, headerInView] = useInView({ once: true, rootMargin: '-80px' });
  const [bonus1Ref, bonus1InView] = useInView({ once: true, rootMargin: '-80px' });
  const [bonus2Ref, bonus2InView] = useInView({ once: true, rootMargin: '-80px' });
  const [onlineRef, onlineInView] = useInView({ once: true, rootMargin: '-80px' });
  const [experienceRef, experienceInView] = useInView({ once: true, rootMargin: '-80px' });
  const [item1Ref, item1InView] = useInView({ once: true, rootMargin: '-80px' });
  const [item2Ref, item2InView] = useInView({ once: true, rootMargin: '-80px' });
  const [item3Ref, item3InView] = useInView({ once: true, rootMargin: '-80px' });
  const [warningRef, warningInView] = useInView({ once: true, rootMargin: '-80px' });
  const [finalRef, finalInView] = useInView({ once: true, rootMargin: '-80px' });

  const headerSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: headerInView ? 1 : 0 },
    config: { duration: 400 }
  });

  const bonus1Spring = useSpring({
    opacity: bonus1InView ? 1 : 0,
    transform: bonus1InView ? 'translateY(0px)' : 'translateY(20px)',
    config: { duration: 400 },
    delay: 0
  });

  const bonus2Spring = useSpring({
    opacity: bonus2InView ? 1 : 0,
    transform: bonus2InView ? 'translateY(0px)' : 'translateY(20px)',
    config: { duration: 400 },
    delay: 50
  });

  const onlineSpring = useSpring({
    opacity: onlineInView ? 1 : 0,
    transform: onlineInView ? 'translateY(0px)' : 'translateY(20px)',
    config: { duration: 400 },
    delay: 100
  });

  const experienceSpring = useSpring({
    opacity: experienceInView ? 1 : 0,
    transform: experienceInView ? 'scale(1)' : 'scale(0.95)',
    config: { duration: 400 }
  });

  const item1Spring = useSpring({
    opacity: item1InView ? 1 : 0,
    transform: item1InView ? 'translateY(0px)' : 'translateY(20px)',
    config: { duration: 400 },
    delay: 0
  });

  const item2Spring = useSpring({
    opacity: item2InView ? 1 : 0,
    transform: item2InView ? 'translateY(0px)' : 'translateY(20px)',
    config: { duration: 400 },
    delay: 50
  });

  const item3Spring = useSpring({
    opacity: item3InView ? 1 : 0,
    transform: item3InView ? 'translateY(0px)' : 'translateY(20px)',
    config: { duration: 400 },
    delay: 100
  });

  const warningSpring = useSpring({
    opacity: warningInView ? 1 : 0,
    transform: warningInView ? 'translateY(0px)' : 'translateY(-20px)',
    config: { duration: 400 }
  });

  const finalSpring = useSpring({
    opacity: finalInView ? 1 : 0,
    config: { duration: 400 }
  });

  const experienceItems = [
    { icon: Hotel, text: "3 noches en Amaroo", ref: item1Ref, spring: item1Spring },
    { icon: Coffee, text: "Desayunos, comidas y cenas", ref: item2Ref, spring: item2Spring },
    { icon: Users, text: "Actividades de integración", ref: item3Ref, spring: item3Spring }
  ];

  return (
    <section id="bonuses" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <animated.div
          ref={headerRef}
          className="text-center mb-16"
          style={headerSpring}
        >
          <p className="text-lg text-[#97d700] font-bold uppercase tracking-wider mb-4">
            Solo por participar
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading uppercase tracking-tight text-black">
            3 BONUS EXCLUSIVOS
            <span className="block text-[#97d700]">valorados en 2.395€ <span className="text-sm font-normal">+IVA</span></span>
          </h2>
        </animated.div>

        {/* Clean bonuses grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
          <animated.div
            ref={bonus1Ref}
            className="bg-gray-50 p-8 rounded-2xl hover:shadow-xl transition-shadow duration-300"
            style={bonus1Spring}
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#97d700]/10 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <Calendar className="w-8 h-8 text-[#97d700]" />
              </div>
              <h3 className="font-heading uppercase tracking-wider text-xl md:text-2xl text-black mb-4">
                {bonuses[0].title}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {bonuses[0].description}
              </p>
            </div>
            <div className="bg-white p-4 rounded-xl text-center">
              <p className="text-sm text-gray-600 mb-1">{bonuses[0].highlight}</p>
              <p className="text-3xl font-heading text-[#97d700]">{bonuses[0].value} <span className="text-sm font-normal">+IVA</span></p>
            </div>
          </animated.div>

          <animated.div
            ref={bonus2Ref}
            className="bg-gray-50 p-8 rounded-2xl hover:shadow-xl transition-shadow duration-300"
            style={bonus2Spring}
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#97d700]/10 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <Map className="w-8 h-8 text-[#97d700]" />
              </div>
              <h3 className="font-heading uppercase tracking-wider text-xl md:text-2xl text-black mb-4">
                {bonuses[1].title}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {bonuses[1].description}
              </p>
            </div>
            <div className="bg-white p-4 rounded-xl text-center">
              <p className="text-sm text-gray-600 mb-1">{bonuses[1].highlight}</p>
              <p className="text-3xl font-heading text-[#97d700]">{bonuses[1].value} <span className="text-sm font-normal">+IVA</span></p>
            </div>
          </animated.div>
        </div>

        {/* Online format included */}
        <animated.div
          ref={onlineRef}
          className="bg-[#97d700]/10 p-8 rounded-2xl max-w-3xl mx-auto mb-16"
          style={onlineSpring}
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-[#97d700]/20 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <Monitor className="w-8 h-8 text-[#97d700]" />
            </div>
            <h3 className="font-heading uppercase tracking-wider text-2xl md:text-3xl text-black mb-4">
              FORMATO ONLINE INCLUÍDO
            </h3>
            <p className="text-lg text-gray-700 mb-6">
              11 módulos, 12 webinars de integración, aula virtual, plantillas de trabajo digitales
            </p>
            <div className="inline-block bg-white px-6 py-3 rounded-xl shadow-md">
              <p className="text-sm text-gray-600">Formación valorada en</p>
              <p className="text-3xl font-heading text-[#97d700]">1.495€ <span className="text-sm font-normal">+IVA</span></p>
            </div>
          </div>
        </animated.div>

        {/* The immersive experience - simplified */}
        <animated.div
          ref={experienceRef}
          className="bg-black text-white p-8 md:p-12 rounded-3xl max-w-4xl mx-auto"
          style={experienceSpring}
        >
          <div className="text-center">
            <div className="inline-block bg-[#97d700] text-black px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-6">
              Solo para los 30 primeros
            </div>

            <h3 className="text-2xl md:text-3xl font-heading uppercase tracking-tight mb-6">
              VIVE LA EXPERIENCIA COMPLETA
            </h3>

            <div className="max-w-2xl mx-auto">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {experienceItems.map((item, index) => (
                  <animated.div
                    key={index}
                    ref={item.ref}
                    style={item.spring}
                    className="text-center"
                  >
                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-2 mx-auto">
                      <item.icon className="w-6 h-6 text-[#97d700]" />
                    </div>
                    <p className="text-gray-300">{item.text}</p>
                  </animated.div>
                ))}
              </div>

              <p className="text-xl text-gray-300 mb-6">
                Todo incluido para que solo te preocupes de tu transformación
              </p>

              <p className="text-3xl font-heading text-[#97d700]">
                Valorado en 525€ <span className="text-sm font-normal">+IVA</span>
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Incluido en tu matrícula
              </p>
            </div>
          </div>
        </animated.div>

        

        {/* Final value proposition */}
        <animated.div
          ref={finalRef}
          className="text-center mt-16"
          style={finalSpring}
        >
          <p className="text-2xl md:text-3xl font-heading uppercase tracking-wider text-black mb-4">
            Valor total bonus: <span className="text-[#97d700]">2.920€ <span className="text-sm font-normal">+IVA</span></span>
          </p>
          <p className="text-xl text-gray-600 mb-8">
            Todo incluido en tu certificación
          </p>
          <a
            href="#registration"
            className="inline-flex items-center gap-2 px-10 py-5 bg-[#97d700] text-black font-heading uppercase tracking-wider font-bold text-xl rounded-full shadow-2xl hover:shadow-[#97d700]/30 transform hover:scale-105 transition-all duration-300"
            aria-label="Aprovechar esta oportunidad - navegar a la sección de registro"
          >
            QUIERO APROVECHAR ESTA OPORTUNIDAD
            <ChevronRight className="w-6 h-6" />
          </a>
        </animated.div>
      </div>
    </section>
  );
}
